import "./roster.css"

import { FaExclamationTriangle } from 'react-icons/fa'
import { analyticsSharp } from 'ionicons/icons'
import {
    useIonAlert,
    IonIcon,
} from '@ionic/react'
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'

import LoadingSpinner from './LoadingSpinner'

import { FadeInBox } from './definitionsPoses'
import { SignInButton } from './Intro'
import {
	calculateRosterNetStats,
	findPairings,
	generateAllNetStats,
} from './functions'
import {
	deepCopy,
	parseData,
	parseRoster,
	sanitizeBest,
	createEmptyPhenotype,
	generateNewPlayerNames,
	onCourtPlayerIds,
} from './util'
import { generateRoster, terminateRosterWorker } from './algorithm/generateRoster'
import {
    useAddToHistory,
    useEditModeState,
    useMyBestState,
	useMyComboStats,
    useMyGame,
    useMyGames,
    useMyGameId,
    useMyLocksState,
    useMyNetStats,
	useMyNumPeriodsLoaded,
    useMyPlayers,
	useMyPlayerNames,
	useMyPreferences,
    useMyRoster,
    useMyRosters,
	useMySeasonRules,
	useMyTeam,
	useSetMyMadeChanges,
    useSetReady,
    useSettings,
    useSetMyRosterIsStale,
    useShowNetStats,
    useShowPositionPreferencesState,
    useStartHistory,
	useUser,
	useMySeasonStatus,
	useMyGameFormat,
	useCanView,
	useMyFillins,
} from './data'
import AwayPlayers from './AwayPlayers'
import ControlBox from './ControlBox'
import DecimalNumber from './DecimalNumber'
import DeleteButton from './DeleteButton'
import DisplayRoster from './DisplayRoster'
import DisplayScores from './DisplayScores'
import { MoreInfoLink } from './MoreInfoLink'
import Orangeman from './Orangeman'
import PositionPreferencesSetting from './PositionPreferencesSetting'
import ProgressBar from './ProgressBar'
import { NORMALIZE_MIN_QTRS_FOR_PAIRS, PREFS_REQUIRE, queryFieldPositions } from "./definitions"
import { evaluateMaleMixedRosterRule } from './maleRules'
import {
	createAutoRosterPhenotype,
	deriveFixedRosterTransitions,
	prepareAutoRosterStart,
	queryAutoRosterSignature,
} from './algorithm/generationState'

function buildOnCourtRostersFromRosters(rosters: Rosters): OnCourtRosters {
	const obj: OnCourtRosters = { }

	Object.keys(rosters).forEach(gameId => {
		const { roster, players, nulled, subs } = rosters[gameId]
		const availablePlayers: {
			[key: string]: boolean
		} = { }

		players.forEach(playerId => {
			availablePlayers[playerId] = true
		})

		const onCourtRoster: RosterDataWithNullablesAndSubs = roster.map(playerId => (playerId && availablePlayers[playerId]) ? [ playerId ] : [ null ])

		nulled?.forEach(positionIndex => {
			onCourtRoster[positionIndex] = [ null ]
		})

		if (subs) {
			for (let i = 0; i < subs.length; i += 2) {
				const [ sub1, sub2 ] = subs.slice(i, i + 2)
				onCourtRoster[sub1].push(onCourtRoster[sub2][0])
				onCourtRoster[sub2].push(onCourtRoster[sub1][0])
			}
		}

		obj[gameId] = onCourtRoster
	})

	return obj
}

function buildComboStatsFromHistory(props: {
	fillins: Players | null
	gameFormat: GameFormat
	games: Games
	numPeriods: NumPeriods
	onCourtRosters: OnCourtRosters
}): ComboStats {
	const { fillins, gameFormat, games, numPeriods, onCourtRosters } = props
	const combo: ComboStats = {
		pairs: { },
		trios: { },
	}

	;([
		{
			mode: 'pairs' as const,
			rows: findPairings({
				games,
				onCourtRosters,
				numPeriods,
				gameFormat,
				fillins,
				findTrios: false,
				userHasPro: true,
			}),
		},
		{
			mode: 'trios' as const,
			rows: findPairings({
				games,
				onCourtRosters,
				numPeriods,
				gameFormat,
				fillins,
				findTrios: true,
				userHasPro: true,
			}),
		},
	] as const).forEach(({ mode, rows }) => {
		rows.forEach(row => {
			let value = row.diff / row.n
			const norm = NORMALIZE_MIN_QTRS_FOR_PAIRS[mode]

			if (row.n < norm) {
				value *= 0.80 / Math.pow(norm - row.n, 1.5)
			}

			combo[mode][row.id] = value
		})
	})

	return combo
}

const Roster = () => {

	const team = useMyTeam()
	const user = useUser()

    const game = useMyGame()
    const gameId = useMyGameId()

    const initialRoster = useMyRoster()

    const setReady = useSetReady()

    const rosters = useMyRosters()
    const games = useMyGames()

    const playerNames = useMyPlayerNames()
	const playerData = useMyPlayers()
	const fillins = useMyFillins()

	//
	// We need to be able to tell when we're loading
	//
	const mySeasonStatus = useMySeasonStatus()
    const loading = mySeasonStatus === 'loading'

    const preferences = useMyPreferences()
    const settings = useSettings()
    const netStats = useMyNetStats()
	const combos = useMyComboStats()
    const showNetStats = useShowNetStats()
	const numPeriods = useMyNumPeriodsLoaded()
	const gameFormat = useMyGameFormat()
	const seasonRules = useMySeasonRules()

	const fieldPositions = queryFieldPositions(gameFormat)

    const [ editMode, setEditMode ] = useEditModeState()
    const setMyRosterIsStale = useSetMyRosterIsStale()

    const [ best, setBest ] = useMyBestState()
    const setMadeChanges = useSetMyMadeChanges()

    const [ showPositionPreferences, setShowPositionPreferences ] = useShowPositionPreferencesState()

    const [ needsSort, setNeedsSort ] = useState(false)

    const [ locks, setLocks ] = useMyLocksState()

    const [ showAltBoard, setShowAltBoard ] = useState(false)

    const addToHistory = useAddToHistory()
    const startHistory = useStartHistory()

	const canView = useCanView()

	const [ progress, setProgress ] = useState(0)
	const autoRosterStartRef = useRef<Best | null>(null)
	const autoRosterCurrentRef = useRef<Best | null>(null)

	const finalizeAutoRoster = useCallback((stopped?: boolean) => {
		setReady(true)
		if (stopped) {
			setProgress(0)
		}

		const didChangeRoster = numPeriods !== null && (
			queryAutoRosterSignature(autoRosterStartRef.current, numPeriods, gameFormat) !==
			queryAutoRosterSignature(autoRosterCurrentRef.current, numPeriods, gameFormat)
		)
		if (didChangeRoster) {
			addToHistory(autoRosterCurrentRef.current || undefined)
			setMyRosterIsStale(false)
			setMadeChanges(true)
		}

		autoRosterStartRef.current = null
		autoRosterCurrentRef.current = null
	}, [ addToHistory, gameFormat, numPeriods, setMadeChanges, setMyRosterIsStale, setReady ])

	const stopAutoRoster = useCallback(() => {
		terminateRosterWorker()
		finalizeAutoRoster(true)
	}, [ finalizeAutoRoster ])

	const [ showAllPlayers, setShowAllPlayers ] = useState(false)

    const [ presentAlert ] = useIonAlert()

	useEffect(() => {
        if (playerNames) {
            setNeedsSort(true)
        }
    }, [ playerNames ])

	useEffect(() => {
		return () => {
			terminateRosterWorker()
		}
	}, [ ])

    //
    // When changing games, reset important things
    //
    useEffect(() => {
        if (gameId) {
            setEditMode(false)
            setShowPositionPreferences(false)
		}
	}, [ gameId, setEditMode, setShowPositionPreferences ])

	type SortThesePlayersProps = {
		players: PlayerId[]
		available: Available
		fillins: Players | null
		onCourtPlayers: PlayerId[]
	}

	const sortThesePlayers = useCallback((props: SortThesePlayersProps) => {
        return props.players.slice().sort((a, b) => {
            let result = Number(props.available[b]) - Number(props.available[a])
            if (!result) {
				result = (props.onCourtPlayers.includes(b) ? 1 : 0) - (props.onCourtPlayers.includes(a) ? 1 : 0)
				if (!result) {
					const aIsDummy = playerNames ? !playerNames[a] : false
					const bIsDummy = playerNames ? !playerNames[b] : false
					result = (aIsDummy ? 2 : props.fillins?.[a] ? 1 : 0) - (bIsDummy ? 2 : props.fillins?.[b] ? 1 : 0)
					if (!result) {
						const aName = (playerNames && playerNames[a]?.toLowerCase()) || a.toLowerCase()
						const bName = (playerNames && playerNames[b]?.toLowerCase()) || b.toLowerCase()
						if (aName > bName) {
							result = 1
						} else if (aName < bName) {
							result = -1
						} else {
							result = 0
						}
					}
				}
			}
            return result
        })
    }, [ playerNames ])

    //
    // When players/availability changes, resort them
    //
    useEffect(() => {
        if (needsSort && numPeriods) {
            setNeedsSort(false)
            const sortedPlayers = sortThesePlayers({
				players: best.players,
				available: best.available,
				fillins,
					onCourtPlayers: onCourtPlayerIds({
						roster: best.roster,
						endRoster: best.endRoster,
						created: best.created,
					endRosterCreated: best.endRosterCreated,
					gameFormat,
					numPeriods,
					subs: best.subs,
				}),
			})

            if (JSON.stringify(sortedPlayers) !== JSON.stringify(best.players)) {
                setBest(sanitizeBest({
                    ...best,
                    players: sortedPlayers,
				}, numPeriods, gameFormat))
            }
        }

	}, [ needsSort, setBest, best, sortThesePlayers, gameFormat, fillins, numPeriods ])

    //
    //
    // Now this is slightly fucked because the database stores 'players' as a list
    // of the players marked available for this roster, while Roster.js uses it as
    // a complete list of all players?
    //
	const calculateInitialValues = useCallback((erase?: boolean) => {

        // console.log('calculateInitialValues', 'playerNames', playerNames, 'initialRoster', initialRoster)

		if (numPeriods === null) {
			return
		}

		// const b: Best = { }

        //
        // Note differences:
        //
        // (A) myPlayers: Object of player objects, including every players registered for this team
        //
        // (B) best.players: Array of playerIds: a simple map from myPlayers
        //
        // (C) initialRoster.players: Array of playerIds, including only those who are 'available' in this roster
        //
        // We need to map (C) to (B) here.
        //

        // Start off by setting up 'available' based on 'myPlayers', with all availability set to false
		const available: Available = { }
        Object.keys(playerNames).forEach(playerId => {
			available[playerId] = false
        })

		const players: Array<PlayerId> = Object.keys(playerNames)

        // Make sure we have a minimum number of players
        let dummyPlayers
        if (players.length < fieldPositions.length) {
            dummyPlayers = generateNewPlayerNames(players, fieldPositions.length - players.length)
            players.push(...dummyPlayers)
        }

        if (initialRoster) {
            // Convert Firestore 'players' (an array of AVAILABLE playerIds) to 'best.players' (an array of ALL playerIds)
            initialRoster.players.forEach(playerId => available[playerId] = true)
        } else {
            // No existing roster, so set everyone to available
            Object.keys(playerNames).forEach(playerId => {
				if (!fillins?.[playerId]) {
					available[playerId] = true
				}
			})
        }

        //
        // nulled records position indexes of rained-out / canceled qtrs.
        //
		let nulled: RosterPositionIndex[] = [ ]

		let subs: Subs = [ ]
		let endRoster: RosterData | undefined
		let endRosterCreated: number | undefined

		let roster: RosterData

        if (erase) {
			roster = [ ]
			endRoster = [ ]
			endRosterCreated = 0
            nulled = [ ]
			subs = [ ]
        } else {
			roster = initialRoster?.roster || [ ]
			endRoster = initialRoster?.endRoster
			endRosterCreated = initialRoster?.endRosterCreated
            nulled = initialRoster?.nulled || [ ]
			subs = initialRoster?.subs || [ ]
        }

        const orangeman = initialRoster?.orangeman || null

		// This must happen AFTER 'players' and 'available' are calculated above.
		const sortedPlayers = sortThesePlayers({
			players,
			available,
			fillins,
				onCourtPlayers: onCourtPlayerIds({
					roster,
					endRoster,
					created: initialRoster?.created,
				endRosterCreated,
				gameFormat,
				numPeriods,
				subs,
			}),
		})

		const b: Best = {
			created: initialRoster?.created || 0,
			players: sortedPlayers,
			available,
			roster,
			endRoster,
			endRosterCreated,
			nulled,
			subs,
			orangeman,
		}

        // console.log("Calculated initial values:", b)

        setMyRosterIsStale(false)

        const newState = sanitizeBest(b, numPeriods, gameFormat)

        setBest(newState)

        startHistory(newState, erase)

	}, [ initialRoster, setBest, setMyRosterIsStale, playerNames, startHistory, sortThesePlayers, numPeriods, gameFormat, fieldPositions.length, fillins ])

    useEffect(() => {
        if (playerNames) {
			calculateInitialValues()
		}
	}, [ calculateInitialValues, gameId, playerNames ])

	const data = useMemo(() => (loading || numPeriods === null) ? undefined : parseRoster({
		roster: best.roster,
		endRoster: best.endRoster,
		players: best.players,
		available: best.available,
		nulled: best.nulled,
		subs: best.subs,
		netStats,
		combos,
		numPeriods,
		gameFormat,
    }), [ loading, best, netStats, combos, gameFormat, numPeriods ])

	const rosterNetStats = useMemo(() => !loading && data && numPeriods !== null && showNetStats && calculateRosterNetStats(data, numPeriods), [ loading, showNetStats, data, numPeriods ])
	const malePlayers = useMemo(() => {
		const obj: {
			[key: PlayerId]: boolean
		} = { }
		Object.values(playerData || { }).forEach(player => {
			obj[player.id] = !!player.isMale
		})
		return obj
	}, [ playerData ])
	const maleMixedRule = useMemo(() => {
		if (numPeriods === null) {
			return {
				hasViolation: false,
				violations: [ ],
				penalty: 0,
			}
		}
		return evaluateMaleMixedRosterRule({
			roster: best.roster,
			endRoster: best.endRoster,
			subs: best.subs,
			nulled: best.nulled,
			numPeriods,
			gameFormat,
			seasonRules,
			malePlayers,
		})
	}, [ best.endRoster, best.nulled, best.roster, best.subs, gameFormat, malePlayers, numPeriods, seasonRules ])

	// console.log('Roster.js render', 'loading', loading, 'numPeriods', numPeriods, 'mySeasonStatus', mySeasonStatus, 'best', best, 'data', data)

	// Still loading...
	if (loading) {
		return <LoadingSpinner />
	}

	if (!canView) {
		return (
			<div className="private-season">
				This season is private.
			</div>
		)
	}

	if (user === null && !team) {
		return (
			<div className="ion-padding">
				<p>
					To view this game, log in or use a shared link.
				</p>
				<p>
					<SignInButton/>
				</p>
			</div>
		)
	}

	if (!game || !Object.keys(game).length) {
        return (
            <div className="ion-padding">
                This game does not exist.
            </div>
        )
    }

	// Don't think this should happen, but we need to stop here if
	// numPeriods is null.
	if (numPeriods === null) {
		return (
			<div className="ion-padding">
				Loading error.
			</div>
		)
	}

    const haveSetPositions = Object.values(preferences).filter(arr => arr && arr.filter(v => v).length).length ? true : false

	function toggleAltBoard() {
        setShowAltBoard(!showAltBoard)
        setShowPositionPreferences(false)
    }

	const setOrangeman = (orangeman: PlayerId | null) => {
        const newState = sanitizeBest({
            ...best,
            orangeman,
		}, numPeriods, gameFormat)
        setBest(newState)
        addToHistory(newState)
        setMadeChanges(true)
    }

	const toggleAvailable = (playerId: PlayerId) => {
        if (!playerNames[playerId]) {
            console.log('Ignoring attempt to toggle availability of dummy player', playerId)
            return
        }

        const available = Object.assign({ }, best.available)
        available[playerId] = !available[playerId]

        const newState = sanitizeBest({
            ...best,
            available,
        }, numPeriods, gameFormat)

        setBest(newState)
        addToHistory(newState)
        setMyRosterIsStale(true)
        setMadeChanges(true)
        setNeedsSort(true)
    }

    function onSwapPlayers() {
        setNeedsSort(true)
    }

	const go = () => {

		if (!gameId) return

        const { orangeman, available } = best
		const fixedTransitions = deriveFixedRosterTransitions({
			roster: best.roster,
			endRoster: best.endRoster,
			subs: best.subs,
			locks,
			numPeriods,
			gameFormat,
		})

		const startingPhenotype = best.roster.length ? createAutoRosterPhenotype({
			...best,
			orangeman,
		}, numPeriods, gameFormat, settings.useRollingSubs ?? 0, fixedTransitions) : {
			...createEmptyPhenotype(best.players, numPeriods, gameFormat),
			orangeman,
		}
		const startingBestState = sanitizeBest(
			prepareAutoRosterStart(best, startingPhenotype, fixedTransitions, numPeriods, gameFormat),
			numPeriods,
			gameFormat,
		)

		//
		// We need to exclude the current game from 'stats' so that
		// we don't include the old version when figuring out things like season balance.
		//
		// FIXME: netStats is probably still buggered though
		//
			const gamesExcludingThisOne: Games = { ...games }
			delete gamesExcludingThisOne[gameId]
			const rostersExcludingThisOne: Rosters = { ...rosters }
			delete rostersExcludingThisOne[gameId]

	        const stats = parseData({
	            players: playerNames,
	            rosters: rostersExcludingThisOne,
				games: gamesExcludingThisOne,
				numPeriods,
				gameFormat,
	        }, true, game?.date)
			const generationNetStats = generateAllNetStats({
				games: gamesExcludingThisOne,
				players: playerNames,
				rosters: rostersExcludingThisOne,
				numPeriods,
				gameFormat,
			}, stats)
			const generationCombos = buildComboStatsFromHistory({
				fillins,
				games: gamesExcludingThisOne,
				onCourtRosters: buildOnCourtRostersFromRosters(rostersExcludingThisOne),
				numPeriods,
				gameFormat,
			})

			autoRosterStartRef.current = deepCopy(best)
			autoRosterCurrentRef.current = deepCopy(startingBestState)
			setBest(startingBestState)

        setShowPositionPreferences(false)
        setMyRosterIsStale(false)

        setReady(false)
        setProgress(0)

        generateRoster({
            onProgress,
			startingPhenotype,
			fixedTransitions,
            players: best.players,
            locks,
            available,
            nulled: best.nulled,
            malePlayers,
            preferences,
            netStats: generationNetStats,
            settings,
            stats,
            combos: generationCombos,
            numPeriods,
            gameFormat,
            seasonRules,
        })
    }

	//
	// Note: 'best' is trapped in a closure and refers to the object before
	// we started changing things, not the most recent iteration!
	//
    function onProgress(newState: Best | ProgressBest) {
        console.log('onProgress', newState)
		if ('progress' in newState) {
			setProgress(newState.progress)
        }
		if ('best' in newState) {
			if (numPeriods === null) {
				console.error('Ignoring generated roster because numPeriods is unavailable.')
				return
			}
			if (!newState.best) {
				return
			}
			const created = Date.now()
			const newBest = sanitizeBest({
                ...best, // i.e. state of 'best' before we clicked "GO"
                ...newState.best,
				endRosterCreated: newState.best.endRoster ? created : undefined,
				created,
			}, numPeriods, gameFormat)
            autoRosterCurrentRef.current = deepCopy(newBest)
            setBest(newBest)
        }
		if ('ready' in newState) {
			setReady(newState.ready)

			if (newState.ready) {
				finalizeAutoRoster()
			}
		}
    }

    function eraseRoster() {
        presentAlert({
            header: 'Erase Roster',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Erase',
                    role: 'destructive',
                    handler: () => {
                        calculateInitialValues(true)
                        setMadeChanges(true)
                        setLocks([ ])
                    },
                },
            ],
        })
    }

    const onCourtPlayers: PlayerId[] = onCourtPlayerIds({
        roster: best.roster,
		endRoster: best.endRoster,
		created: best.created,
		endRosterCreated: best.endRosterCreated,
        gameFormat,
        numPeriods,
        subs: best.subs,
    })

    const onCourtPlayerSet = new Set(onCourtPlayers)
    const numRows = best.players.filter(playerId => best.available[playerId] || onCourtPlayerSet.has(playerId)).length

    const DEFAULT_SHOW_NUM_PLAYERS = 9
    const showRequireModeWarning = showPositionPreferences && settings.preferenceType === PREFS_REQUIRE
    const showMaleMixedAlert = maleMixedRule.hasViolation && !showPositionPreferences

    const numPlayersShown = (editMode && !showAllPlayers && !showPositionPreferences) ?
        Math.max(

            // Players that are either available or currently on court
            numRows,

            // Default, but don't hide just 1 player
            best.players.length === DEFAULT_SHOW_NUM_PLAYERS + 1 ? DEFAULT_SHOW_NUM_PLAYERS + 1 : DEFAULT_SHOW_NUM_PLAYERS

    ) : 99

    const myClasses = [
        'roster-container',
        `roster-${editMode ? 'un' : ''}locked`,
    ]

    if (showPositionPreferences) {
        myClasses.push('roster-position-preferences')
    } else {
        if (showAltBoard && editMode) {
            myClasses.push('roster-edit-altboard')
        }
        if (showAltBoard) {
            myClasses.push('roster-altboard')
        }
    }

    if (numRows >= 13) {
        myClasses.push('roster-print-verycompact')
    } else if (numRows >= 11) {
        myClasses.push('roster-print-compact')
    }

    const content = (
        <div className={myClasses.join(' ')}>
            <ControlBox
                haveSetPositions={haveSetPositions}
                showAltBoard={showAltBoard}
                onToggleAltBoard={() => { toggleAltBoard() }}
                onAutoRoster={go}
                onStopAutoRoster={stopAutoRoster}
            />
            <ProgressBar
                variant="determinate"
                value={loading ? 20 : progress}
            />
            <Warnings />
            <div className="roster-board-shell">
                {
                    (showMaleMixedAlert || showRequireModeWarning) ? (
                        <div className="roster-alert-stack">
                            {
                                showMaleMixedAlert ? (
                                    <div className="roster-rules-alert roster-rules-alert-visible">
                                        <MoreInfoLink text="Too many males." helpSection="tips" helpSubSection="rules" />
                                    </div>
                                ) : null
                            }
                            {
                                showRequireModeWarning ? (
                                    <div className="roster-rules-alert roster-require-alert roster-rules-alert-visible">
                                        <MoreInfoLink text="Require mode on." helpSection="tips" helpSubSection="prefs" />
                                    </div>
                                ) : null
                            }
                        </div>
                    ) : null
                }
                <DisplayRoster
                    data={data}
                    loading={loading}
                    toggleAvailable={toggleAvailable}
                    showAltBoard={showAltBoard}
                    showPositionPreferences={showPositionPreferences}
                    setOrangeman={setOrangeman}
                    onSwapPlayers={onSwapPlayers}
                    numPlayersShown={numPlayersShown}
                    showAllPlayers={showAllPlayers}
                    setShowAllPlayers={setShowAllPlayers}
                    onCourtPlayers={onCourtPlayers}
                />
            </div>
            {
                showPositionPreferences && (
                    <PositionPreferencesSetting
                        showPositionPreferences={showPositionPreferences}
                    />
                )
            }
            <DisplayScores />
            {
                showNetStats && rosterNetStats && (
                    <RateNetStats rosterNetStats={rosterNetStats} />
                )
            }
            {
                !editMode && (
                    <AwayPlayers />
                )
            }
            <div className="underneath-grid">
                <Orangeman
                    setOrangeman={setOrangeman}
                />

                <DeleteButton
                    show={editMode}
                    onClick={eraseRoster}
                />

            </div>

        </div>
    )

    return (
        <FadeInBox pose={best?.roster ? 'visible' : 'hidden'}>
            {content}
        </FadeInBox>
    )

}

type RateNetStatsProps = {
	rosterNetStats: RosterNetStats
}

const RateNetStats = (props: RateNetStatsProps) => {

    const { rosterNetStats } = props

	const numPeriods = useMyNumPeriodsLoaded()

    if (!rosterNetStats || !numPeriods)
        return null

    const { qtrs, total } = rosterNetStats

    return (
        <div className="undergrid undergrid-netstats">
            <div className={`grid grid-${numPeriods}`}>
                <div className="column column-players">
                    <div className="fixed-element">
                        <div className={`cell cell-position cell-void cell-netstat cell-netstat-${total < 0 ? 'negative' : 'positive'}`}>
                            <IonIcon icon={analyticsSharp} />
                            <DecimalNumber
                                value={total}
                                isComparison={true}
                            />
                        </div>
                    </div>
                </div>
                {
                    qtrs.map((score, index) => (
                        <div className="column column-positions" key={index}>
                            <div className="fixed-element">
                                <RateNetStatsQtr
                                    key={index}
                                    score={score}
                                    className="cell cell-position cell-void"
                                />
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    )
}

type RateNetStatsQtrProps = {
	score: number | null
	className?: string
}

const RateNetStatsQtr = (props: RateNetStatsQtrProps) => {

    const { score, className } = props

    return (
        <div className={(className || '') + ` cell-netstat cell-netstat-${(score && score < 0) ? 'negative' : 'positive'}`}>
            <DecimalNumber
                value={score}
                isComparison={true}
            />
        </div>
    )
}

const Warnings = () => {

    const players = useMyPlayers()
    const roster = useMyRoster()

	const warnings = [ ]

	if (players && !Object.keys(players).length) {
		warnings.push("Add players before creating a roster.")
	} else if (roster?.players && !roster.players.length) {
		warnings.push("No players are marked available.")
	}

    if (!warnings.length)
        return null

    return (
		<>
			{
				warnings.map((warning, index) => <Warning key={index}>{warning}</Warning>)
			}
		</>
	)
}

export const Warning = (props: React.PropsWithChildren) => {

    return (
		<div className="roster-warning">
			<span>
				<FaExclamationTriangle />
				{' '}
				{props.children}
			</span>
			{' '}
		</div>
	)
}

export default Roster
